import React from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Wrench, Flame } from "lucide-react";
import { SERVICES, BUSINESS } from "../../data/business";

// Unified replacement for the old "Common plumbing problems" + "What we do" sections.
// Less text, more visuals, one section instead of two. Layout:
//   • Top: short heading + sub
//   • Left rail (lg): a single "featured service" spotlight (drain cleaning)
//   • Right grid:    six service tiles, each with icon + name + one-line short
//   • Bottom: "View all 14 services" link
const HIGHLIGHT_SLUGS = [
  "drain-cleaning", // featured spotlight (rendered separately at lg+)
  "hydro-jetting",
  "sewer-camera-inspection",
  "pipe-descaling",
  "sewer-waterline-replacement",
  "sump-pump-replacement",
  "basement-waterproofing",
];

function getService(slug) {
  return SERVICES.find((s) => s.slug === slug);
}

function ServiceTile({ service }) {
  const Icon = LucideIcons[service.icon] || Wrench;
  return (
    <Link
      to={`/services/${service.slug}`}
      className="abc-card p-5 sm:p-6 block group transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(26,29,33,0.12)]"
      data-testid={`fix-service-${service.slug}`}
    >
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EDEFF2] text-[#54616E] group-hover:bg-[#54616E] group-hover:text-white transition-colors">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </span>
      <h3 className="font-display font-bold text-[#1A1D21] text-base sm:text-lg mt-3">{service.name}</h3>
      <p className="text-[13.5px] text-slate-600 mt-1.5 leading-relaxed line-clamp-2">{service.short}</p>
    </Link>
  );
}

function FeaturedSpotlight() {
  const wh = getService("drain-cleaning");
  if (!wh) return null;
  return (
    <Link
      to={`/services/${wh.slug}`}
      className="relative rounded-3xl overflow-hidden group block h-full min-h-[260px] lg:min-h-[420px]"
      data-testid="fix-featured-spotlight"
    >
      <img
        src="https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=900"
        alt="Drain Sights technician working on a drain line"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        loading="lazy"
      />
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, rgba(26,29,33,0.20) 0%, rgba(26,29,33,0.55) 60%, rgba(26,29,33,0.92) 100%)",
        }}
      />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-7 text-white">
        <div className="inline-flex items-center gap-1.5 text-[#E31D2B] text-[11px] font-bold uppercase tracking-[0.16em]">
          <Flame className="w-3.5 h-3.5" /> Featured Service
        </div>
        <h3 className="font-display font-extrabold text-2xl sm:text-3xl mt-2 leading-tight">{wh.name}</h3>
        <p className="text-white/80 text-sm mt-2 max-w-sm leading-relaxed">{wh.short}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold">
          Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function WhatWeFixSection() {
  const tiles = HIGHLIGHT_SLUGS.slice(1).map(getService).filter(Boolean);
  return (
    <section className="py-14 md:py-20 abc-bg-pale" data-testid="home-what-we-fix">
      <div className="abc-container">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-3">What we fix</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21] leading-tight">
            Common drain &amp; sewer problems, <span className="text-[#E31D2B]">solved cleanly</span>.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            From a slow drain to a collapsed sewer line — the drain, sewer, and basement-water work homes across the Baltimore and DC metros actually need. Pick the one that sounds like your house.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-5 lg:gap-6">
          <div className="lg:col-span-5">
            <FeaturedSpotlight />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
            {tiles.map((s) => <ServiceTile key={s.slug} service={s} />)}
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-slate-500">Don't see your issue? If it's a drain, sewer, or basement-water problem, give us a call and we'll talk it through.</p>
          <div className="flex flex-wrap items-center gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-outline" data-testid="fix-call-cta">
              Call {BUSINESS.phone}
            </a>
            <Link to="/services" className="abc-btn abc-btn-accent" data-testid="fix-all-services-cta">
              View all {SERVICES.length} services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
